import {
  Component,
  lazy,
  type ReactNode,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

import { t } from '../i18n'
import type { BoardScenePayload } from './types'

/**
 * Lazy-loaded Excalidraw board. Kept out of the join-path bundle — the first
 * time a teacher opens the board we pay the cost; until then the class is light.
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ExcalidrawAPI = any

/** How long after applying a remote scene we ignore our own onChange. */
const REMOTE_ECHO_WINDOW_MS = 250
/** One published snapshot per pause in drawing, not per pointer move. */
const PUBLISH_DEBOUNCE_MS = 280

const ExcalidrawLazy = lazy(async () => {
  const mod = await import('@excalidraw/excalidraw')
  await import('@excalidraw/excalidraw/index.css')
  const { Excalidraw, MainMenu } = mod

  /**
   * Excalidraw with NO main menu.
   *
   * Left alone, it renders its own hamburger holding Export image / Find on
   * canvas / Help and then outbound links to Excalidraw's GitHub, X and
   * Discord — an exit route out of a child's lesson into unmoderated public
   * chat, one tap from the drawing tools. None of it is classroom function.
   *
   * There is no documented "no menu" switch, so this is the two supported
   * halves of the answer together. Passing our own `<MainMenu>` is what the
   * API offers: per the docs, "if you render it, you will need to populate the
   * menu with your own items as we will not render any ourselves at that
   * point" — so an EMPTY one means Excalidraw builds none of its defaults.
   * That is the half that matters: the links stop existing in the DOM rather
   * than merely being painted over. The trigger button has no API of its own
   * and is hidden in theme.css.
   *
   * Deliberately belt-and-braces: if a future Excalidraw release renames the
   * class that CSS keys on, the worst case is an empty menu button coming
   * back — never the social links.
   */
  const BoardWithoutMenu = (props: Record<string, unknown>) => (
    <Excalidraw {...props}>
      <MainMenu />
    </Excalidraw>
  )
  return { default: BoardWithoutMenu }
})

interface Props {
  /** Students cannot draw; teachers can. */
  viewOnly: boolean
  /** Remote or restored scene. Null means keep current. */
  scene: BoardScenePayload | null
  sceneVersion: number
  onSceneChange: (scene: BoardScenePayload) => void
  /** Keep the latest scene without broadcasting (local cache). */
  onSceneLocal?: (scene: BoardScenePayload) => void
  /** Publish failure, shown to the teacher only. */
  error?: string | null
}

export function Whiteboard({
  viewOnly,
  scene,
  sceneVersion,
  onSceneChange,
  onSceneLocal,
  error,
}: Props) {
  const apiRef = useRef<ExcalidrawAPI | null>(null)
  /**
   * Deadline, not a boolean.
   *
   * The first version set a flag and cleared it in requestAnimationFrame. rAF does
   * not fire in a backgrounded tab, and a teacher alt-tabbing to VS Code mid-lesson
   * is the normal teaching flow — so a remote scene landing while they were away
   * left the flag stuck ON, and every edit they made afterwards was silently
   * dropped by the guard below. The board simply stopped syncing until remount.
   *
   * A deadline cannot latch: it expires whether or not any callback runs.
   */
  const suppressUntil = useRef(0)
  const debounceRef = useRef<number | null>(null)
  const [ready, setReady] = useState(false)

  // Apply a remote scene without echoing it straight back out through onChange.
  useEffect(() => {
    if (!apiRef.current || !scene) return
    // Excalidraw fires onChange in response to updateScene, sometimes across a
    // couple of frames, so the window covers slightly more than one tick.
    suppressUntil.current = Date.now() + REMOTE_ECHO_WINDOW_MS
    try {
      apiRef.current.updateScene({
        elements: scene.elements,
        // Only force appState when the sender supplied one — otherwise every
        // remote tick would yank the teacher's own scale and scroll position.
        ...(scene.appState ? { appState: scene.appState } : {}),
        commitToHistory: false,
      })
    } catch {
      /* a malformed scene must not take the board down */
    }
  }, [scene, sceneVersion])

  const handleChange = useCallback(
    (elements: readonly unknown[], appState: { viewBackgroundColor?: string }) => {
      if (viewOnly || Date.now() < suppressUntil.current) return
      const payload: BoardScenePayload = {
        elements: elements as unknown[],
        appState: { viewBackgroundColor: appState.viewBackgroundColor },
      }
      onSceneLocal?.(payload)
      // Excalidraw fires onChange on every pointer move while drawing. Publishing
      // a full scene at that rate would saturate the data channel, so the wire
      // sees one snapshot per pause; the local cache above stays current.
      if (debounceRef.current) window.clearTimeout(debounceRef.current)
      debounceRef.current = window.setTimeout(() => onSceneChange(payload), PUBLISH_DEBOUNCE_MS)
    },
    [viewOnly, onSceneChange, onSceneLocal],
  )

  useEffect(
    () => () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current)
    },
    [],
  )

  // Open the teacher ON the freehand pen instead of Excalidraw's default selection
  // tool. A teacher who opens a "whiteboard" and drags expects a line; on selection
  // they get a rubber-band that draws nothing, which reads as "the board is
  // broken" — and was the reported bug.
  //
  // This has to run AFTER Excalidraw finishes its own init: setting the tool
  // inside the excalidrawAPI callback gets overwritten when Excalidraw settles on
  // selection a tick later. Two deferred attempts cover that init timing without a
  // permanent interval fighting the teacher once they pick their own tool. Students
  // are view-only and keep no tool.
  useEffect(() => {
    if (!ready || viewOnly) return
    // The first shot claims the tool as soon as we can; the second only fires if
    // the board is STILL on selection — i.e. Excalidraw's init reset landed late,
    // or the teacher has not touched a tool yet. If they already picked rectangle or
    // text in that quarter-second, `respectChoice` leaves it alone rather than
    // yanking it back to the pen.
    const setPen = (respectChoice: boolean) => {
      try {
        const api = apiRef.current
        if (!api) return
        if (respectChoice && api.getAppState()?.activeTool?.type !== 'selection') return
        api.setActiveTool({ type: 'freedraw' })
      } catch {
        /* older API shape — the board still works, just starts on selection */
      }
    }
    const first = window.setTimeout(() => setPen(false), 0)
    const second = window.setTimeout(() => setPen(true), 250)
    return () => {
      window.clearTimeout(first)
      window.clearTimeout(second)
    }
  }, [ready, viewOnly])

  return (
    // dir=ltr because the board holds code and diagrams. The chrome around it
    // stays RTL; only the canvas is forced.
    <div className="whiteboard" dir="ltr">
      <BoardLoadBoundary>
        <Suspense
          fallback={
            <div className="whiteboard__loading" role="status">
              {t('surface.boardLoading')}
            </div>
          }
        >
          <ExcalidrawLazy
            excalidrawAPI={(api: ExcalidrawAPI) => {
              apiRef.current = api
              setReady(true)
            }}
            viewModeEnabled={viewOnly}
            zenModeEnabled={false}
            gridModeEnabled={false}
            theme="dark"
            UIOptions={{
              canvasActions: {
                loadScene: false,
                export: false,
                saveAsImage: !viewOnly,
                clearCanvas: false,
                toggleTheme: false,
                changeViewBackgroundColor: !viewOnly,
              },
              tools: {
                image: false,
              },
            }}
            onChange={handleChange as never}
          />
        </Suspense>
      </BoardLoadBoundary>
      {ready && viewOnly ? (
        <span className="whiteboard__badge">{t('surface.viewOnly')}</span>
      ) : null}
      {error ? (
        <p className="whiteboard__error" role="alert">
          {t(error === 'tooLarge' ? 'surface.boardTooLarge' : 'surface.boardFailed')}
        </p>
      ) : null}
    </div>
  )
}

/**
 * The board arrives as a lazy chunk over the same congested connection carrying
 * the lesson. Without this, a failed fetch throws inside Suspense and takes the
 * whole classroom down — video included — over a feature nobody had opened yet.
 */
class BoardLoadBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  render() {
    if (this.state.failed) {
      return (
        <div className="whiteboard__loading" role="alert">
          {t('surface.boardFailedToLoad')}
        </div>
      )
    }
    return this.props.children
  }
}
